import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Minus, RefreshCw, X, Clock, CheckCircle,
  Zap, Building2, Globe, ShieldCheck, ChevronDown, ChevronUp,
  Smartphone, Bell, FileCheck, Users, Layers, BarChart3, Lock
} from 'lucide-react';
import { formatDate } from '../lib/dateFormat';

/* ── Palette (earthy/organic — from design guidelines) ─────────────────────── */
const C = {
  bg:       '#F8F6F3',
  bg2:      '#EAE5DF',
  text:     '#2D3728',
  text2:    '#5C6A53',
  accent:   '#C85A32',
  accentHv: '#A64927',
  gold:     '#E8B25A',
  border:   '#D8D2C9',
  success:  '#477242',
  dark:     '#2D3728',
};

/* ── Plan definitions ────────────────────────────────────────────────────────── */
const PLANS = [
  {
    id: 'starter', monthlyId: 'starter_monthly', annualId: 'starter_annual',
    name: 'Starter', tagline: 'My first POS',
    monthlyPhp: 499, annualPhp: 4990,
    branches: 1, users: 3, products: '500',
    highlight: false, dark: false,
    icon: Zap,
    color: '#C85A32',
    extraBranchAvailable: false,
  },
  {
    id: 'business', monthlyId: 'business_monthly', annualId: 'business_annual',
    name: 'Business', tagline: 'Serious single store',
    monthlyPhp: 1499, annualPhp: 14990,
    branches: 1, users: 10, products: '∞',
    highlight: false, dark: false,
    icon: Building2,
    color: '#477242',
    extraBranchAvailable: false,
  },
  {
    id: 'professional', monthlyId: 'professional_monthly', annualId: 'professional_annual',
    name: 'Professional', tagline: 'Multi-branch operations',
    monthlyPhp: 2999, annualPhp: 29990,
    branches: '1+', users: 30, products: '∞',
    highlight: true, dark: true,
    icon: Globe,
    color: '#E8B25A',
    extraBranchPhp: 2000,
    extraBranchAvailable: true,
  },
  {
    id: 'enterprise', monthlyId: 'enterprise_monthly', annualId: 'enterprise_annual',
    name: 'Enterprise', tagline: 'Full operations grade',
    monthlyPhp: 4999, annualPhp: 49990,
    branches: '1+', users: '∞', products: '∞',
    highlight: false, dark: false,
    icon: ShieldCheck,
    color: '#2D3728',
    extraBranchPhp: 3000,
    extraBranchAvailable: true,
  },
];

/* ── Feature comparison data ────────────────────────────────────────────────── */
const FEATURE_GROUPS = [
  {
    category: 'Core POS', icon: Zap,
    features: [
      { label: 'POS + Cash / Split Payments', plans: [true, true, true, true] },
      { label: 'Customer DB + AR + Credit Terms', plans: [true, true, true, true] },
      { label: 'GCash / Maya / Bank Digital Wallets', plans: [true, true, true, true] },
      { label: 'Offline POS (works without internet)', plans: [true, true, true, true] },
      { label: 'Returns & Refunds', plans: [true, true, true, true] },
      { label: 'Draft / Parked Sales', plans: [false, true, true, true] },
      { label: 'Price Schemes (Retail / Wholesale)', plans: [true, true, true, true] },
    ],
  },
  {
    category: 'Inventory', icon: Layers,
    features: [
      { label: 'Product Catalog', plans: ['500 products', '∞', '∞', '∞'] },
      { label: 'Stock Tracking + Low Stock Alerts', plans: [true, true, true, true] },
      { label: 'Repack / Bulk → Unit Splitting', plans: [true, true, true, true] },
      { label: 'Branch Transfer Pricing', plans: [false, false, true, true] },
      { label: 'Price Manager + Barcode Printing', plans: [false, true, true, true] },
      { label: 'Count Sheets (Inventory Audit)', plans: [false, true, true, true] },
    ],
  },
  {
    category: 'Finance & Accounting', icon: BarChart3,
    features: [
      { label: 'Fund Wallets', plans: ['2 (Cashier + Safe)', '4-Wallet', '4-Wallet', '4-Wallet'] },
      { label: 'Purchase Orders + Receiving', plans: [false, true, true, true] },
      { label: 'Pay Supplier + AP Management', plans: [false, true, true, true] },
      { label: 'Employee Management + Cash Advances', plans: [false, true, true, true] },
      { label: 'Expense Tracking', plans: [true, true, true, true] },
      { label: 'Z-Report PDF', plans: [false, true, true, true] },
      { label: 'Multi-Cashier Shifts + Variance', plans: [false, true, true, true] },
      { label: 'Advanced Financial Reports', plans: [false, true, true, true] },
    ],
  },
  {
    category: 'Communications', icon: Smartphone,
    features: [
      { label: 'SMS Gateway (unlimited via own SIM)', plans: [false, true, true, true] },
      { label: 'Telegram Notifications (12 hooks)', plans: [false, true, true, true] },
      { label: 'SMS Conversations + Templates', plans: [false, true, true, true] },
      { label: 'Automated Trial / Grace Reminders', plans: [false, true, true, true] },
    ],
  },
  {
    category: 'Multi-Branch', icon: Building2,
    features: [
      { label: 'Multi-Branch Management', plans: [false, false, true, true] },
      { label: 'Branch Transfers + Stock Requests', plans: [false, false, true, true] },
      { label: 'Terminal Pairing (AgriSmart Android)', plans: [false, false, true, true] },
      { label: 'Cross-Branch Payments + AR', plans: [false, false, true, true] },
      { label: 'Internal Invoices (inter-branch)', plans: [false, false, true, true] },
      { label: 'Branch Pickups', plans: [false, false, true, true] },
    ],
  },
  {
    category: 'BIR Compliance & Audit', icon: FileCheck,
    features: [
      { label: 'BIR Compliance Mode (OR / SI)', plans: [false, false, true, true] },
      { label: 'BIR Z-Report (sequential BIRZ#)', plans: [false, false, true, true] },
      { label: 'Official Collection Receipts (CR#)', plans: [false, false, true, true] },
      { label: 'VAT Computation (12% back-calc)', plans: [false, false, true, true] },
      { label: 'Basic Audit Trail', plans: [false, true, true, true] },
      { label: 'Full Audit Center', plans: [false, false, true, true] },
      { label: 'Transaction Verification + Drift', plans: [false, false, false, true] },
      { label: 'Balance Reconciliation', plans: [false, false, false, true] },
    ],
  },
  {
    category: 'Security & Admin', icon: Lock,
    features: [
      { label: 'Manager Approval Workflow', plans: [true, true, true, true] },
      { label: 'Granular Role Permissions', plans: [false, false, true, true] },
      { label: 'Incident Tickets', plans: [false, false, true, true] },
      { label: 'TOTP / 2FA Authentication', plans: [false, false, false, true] },
      { label: 'Capital Change Report', plans: [false, false, false, true] },
      { label: 'Priority Support', plans: [false, false, false, true] },
    ],
  },
];

/* ── QR Checkout Dialog ──────────────────────────────────────────────────────── */
function QRCheckoutDialog({ plan, annual, extraBranches, onClose, onSuccess }) {
  const planId = annual ? plan.annualId : plan.monthlyId;
  const [step, setStep] = useState('loading'); // loading | qr | polling | success | error
  const [invoice, setInvoice] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 min
  const [errMsg, setErrMsg] = useState('');
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const totalPhp = annual
    ? (plan.annualPhp + (plan.extraBranchAvailable ? extraBranches * plan.extraBranchPhp * 10 : 0))
    : (plan.monthlyPhp + (plan.extraBranchAvailable ? extraBranches * (plan.extraBranchPhp || 0) : 0));

  // Start checkout
  useEffect(() => {
    (async () => {
      try {
        const r = await api.post('/billing/checkout', { plan_id: planId, extra_branches: extraBranches });
        setInvoice(r.data);
        setStep('qr');
        // 30-min countdown
        timerRef.current = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
        // Poll every 4s
        pollRef.current = setInterval(async () => {
          try {
            const s = await api.get(`/billing/status/${r.data.invoice_id}`);
            if (s.data.status === 'paid') {
              clearInterval(pollRef.current);
              clearInterval(timerRef.current);
              setStep('success');
              onSuccess();
            } else if (s.data.status === 'failed') {
              clearInterval(pollRef.current);
              setErrMsg('Payment failed or was canceled. Please try again.');
              setStep('error');
            }
          } catch {}
        }, 4000);
      } catch (e) {
        setErrMsg(e.response?.data?.detail || 'Failed to create checkout. Please try again.');
        setStep('error');
      }
    })();
    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, [planId, extraBranches]);

  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="qr-checkout-dialog"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ fontFamily: 'Manrope, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.text2 }}>QR Ph Payment</p>
            <p className="font-black text-lg" style={{ color: C.text }}>
              {plan.name} · {annual ? 'Annual' : 'Monthly'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100">
            <X size={16} style={{ color: C.text2 }} />
          </button>
        </div>

        {/* Amount bar */}
        <div className="px-6 py-3 flex items-center justify-between" style={{ backgroundColor: C.bg2 }}>
          <span className="text-sm" style={{ color: C.text2 }}>
            {extraBranches > 0 ? `Base + ${extraBranches} extra branch${extraBranches > 1 ? 'es' : ''}` : 'Total due'}
          </span>
          <span className="text-xl font-black" style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>
            ₱{totalPhp.toLocaleString()}
          </span>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {step === 'loading' && (
            <div className="text-center py-12 space-y-3">
              <RefreshCw size={32} className="animate-spin mx-auto" style={{ color: C.accent }} />
              <p className="text-sm" style={{ color: C.text2 }}>Generating your QR code…</p>
            </div>
          )}

          {step === 'qr' && invoice && (
            <div className="space-y-4">
              {/* QR image */}
              <div className="flex flex-col items-center">
                <div
                  className="p-4 rounded-2xl border-2"
                  style={{ borderColor: C.border, backgroundColor: C.bg }}
                >
                  {invoice.qr_image_url ? (
                    <img
                      src={invoice.qr_image_url}
                      alt="Scan to pay"
                      className="w-52 h-52 object-contain"
                      data-testid="qr-code-image"
                    />
                  ) : (
                    <div className="w-52 h-52 flex items-center justify-center bg-slate-100 rounded-lg">
                      <p className="text-xs text-slate-400 text-center px-4">QR code not available. Please try again.</p>
                    </div>
                  )}
                </div>
                <p className="text-xs mt-2 font-bold uppercase tracking-widest" style={{ color: C.text2 }}>
                  Scan with GCash · Maya · BDO · BPI · UnionBank
                </p>
              </div>

              {/* Timer */}
              <div className="flex items-center justify-center gap-2">
                <Clock size={14} style={{ color: timeLeft < 300 ? '#dc2626' : C.text2 }} />
                <span
                  className="font-mono text-lg font-bold"
                  style={{
                    color: timeLeft < 300 ? '#dc2626' : C.text,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {mins}:{secs}
                </span>
                <span className="text-xs" style={{ color: C.text2 }}>remaining</span>
              </div>

              {/* Polling indicator */}
              <div
                className="flex items-center justify-center gap-2 py-2 px-4 rounded-full"
                style={{ backgroundColor: C.bg2 }}
              >
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-medium" style={{ color: C.text2 }}>
                  Waiting for payment confirmation…
                </span>
              </div>

              <p className="text-[11px] text-center" style={{ color: C.text2 }}>
                QR code expires in {mins}:{secs}. Payment auto-confirms — no manual upload needed.
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8 space-y-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
                style={{ backgroundColor: '#dcfce7' }}
              >
                <CheckCircle size={40} style={{ color: C.success }} />
              </div>
              <div>
                <p className="font-black text-xl" style={{ color: C.text }}>Payment Confirmed!</p>
                <p className="text-sm mt-1" style={{ color: C.text2 }}>
                  Your <strong>{plan.name}</strong> plan is now active.
                </p>
              </div>
              <Button
                className="w-full"
                style={{ backgroundColor: C.success, color: 'white' }}
                onClick={onClose}
                data-testid="qr-success-close-btn"
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto bg-red-50">
                <X size={28} className="text-red-500" />
              </div>
              <p className="text-sm font-semibold" style={{ color: C.text }}>{errMsg}</p>
              <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Feature cell helper ─────────────────────────────────────────────────────── */
function FeatureCell({ val, planIdx }) {
  const darkPlan = planIdx === 2;
  if (val === true) return (
    <div className="flex justify-center">
      <Check size={16} style={{ color: planIdx === 2 ? C.gold : C.success }} />
    </div>
  );
  if (val === false) return (
    <div className="flex justify-center">
      <Minus size={14} style={{ color: darkPlan ? 'rgba(248,246,243,0.3)' : C.border }} />
    </div>
  );
  return (
    <div className="text-center text-[11px] font-medium" style={{ color: darkPlan ? C.gold : C.text }}>
      {val}
    </div>
  );
}

/* ── Addon Bundle Checkout Dialog ────────────────────────────────────────────── */
function AddonCheckoutDialog({ bundle, annual, onClose, onSuccess }) {
  const [step, setStep] = useState('loading');
  const [invoice, setInvoice] = useState(null);
  const [timeLeft, setTimeLeft] = useState(30 * 60);
  const [errMsg, setErrMsg] = useState('');
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const monthlyPrice = bundle.addon_price_php || 0;
  const totalPhp = annual ? Math.round(monthlyPrice * 10) : monthlyPrice;
  const planId = annual ? `${bundle.id}_annual` : `${bundle.id}_monthly`;

  useEffect(() => {
    (async () => {
      try {
        // For add-on bundles, use a special checkout that enables the bundle
        // For now, use the billing checkout with the bundle's pricing
        // The plan_id maps to a billing plan for the add-on
        const r = await api.post('/billing/checkout', {
          plan_id: `addon_${bundle.id}`,
          extra_branches: 0,
          addon_bundle_id: bundle.id,
          amount_override: totalPhp * 100, // centavos
          description: `${bundle.label} Add-on Bundle`,
        });
        setInvoice(r.data); setStep('qr');
        timerRef.current = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
        pollRef.current = setInterval(async () => {
          try {
            const s = await api.get(`/billing/status/${r.data.invoice_id}`);
            if (s.data.status === 'paid') {
              clearInterval(pollRef.current); clearInterval(timerRef.current);
              setStep('success'); onSuccess();
            }
          } catch {}
        }, 4000);
      } catch (e) {
        setErrMsg(e.response?.data?.detail || 'Failed to create checkout. Please contact support.');
        setStep('error');
      }
    })();
    return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); };
  }, []);

  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="px-6 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.text2 }}>Add-on Bundle</p>
            <p className="font-black text-lg" style={{ color: C.text }}>{bundle.label}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X size={16} style={{ color: C.text2 }} /></button>
        </div>
        <div className="px-6 py-3 flex items-center justify-between" style={{ backgroundColor: C.bg2 }}>
          <span className="text-sm" style={{ color: C.text2 }}>{annual ? 'Annual' : 'Monthly'}</span>
          <span className="text-xl font-black" style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>₱{totalPhp.toLocaleString()}</span>
        </div>
        <div className="px-6 py-6">
          {step === 'loading' && <div className="text-center py-8"><RefreshCw size={28} className="animate-spin mx-auto" style={{ color: C.accent }} /></div>}
          {step === 'qr' && invoice && (
            <div className="space-y-4">
              <div className="flex flex-col items-center">
                <div className="p-4 rounded-2xl border-2" style={{ borderColor: C.border }}>
                  {invoice.qr_image_url
                    ? <img src={invoice.qr_image_url} alt="Scan to pay" className="w-48 h-48 object-contain" />
                    : <div className="w-48 h-48 bg-slate-50 rounded flex items-center justify-center text-xs text-slate-400">QR not available</div>}
                </div>
                <p className="text-xs mt-2 font-bold uppercase tracking-widest" style={{ color: C.text2 }}>GCash · Maya · BDO · BPI</p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Clock size={14} style={{ color: timeLeft < 300 ? '#dc2626' : C.text2 }} />
                <span className="font-mono text-lg font-bold" style={{ color: timeLeft < 300 ? '#dc2626' : C.text, fontVariantNumeric: 'tabular-nums' }}>{mins}:{secs}</span>
              </div>
              <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-full" style={{ backgroundColor: C.bg2 }}>
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-medium" style={{ color: C.text2 }}>Waiting for payment…</span>
              </div>
            </div>
          )}
          {step === 'error' && (
            <div className="text-center py-6 space-y-3">
              <p className="text-sm font-semibold" style={{ color: C.text }}>{errMsg}</p>
              <p className="text-xs" style={{ color: C.text2 }}>Contact support to manually enable this add-on bundle for your account.</p>
              <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Main page ───────────────────────────────────────────────────────────────── */
export default function UpgradePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  const [extraBranches, setExtraBranches] = useState(0);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [checkoutAddon, setCheckoutAddon] = useState(null); // addon bundle checkout
  const [subscription, setSubscription] = useState(null);
  const [addonBundles, setAddonBundles] = useState([]);
  const [tableExpanded, setTableExpanded] = useState(false);
  const [autoRenewSaving, setAutoRenewSaving] = useState(false);

  const currentPlan = user?.subscription?.effective_plan || user?.subscription?.plan || 'trial';
  const activeAddonBundles = user?.subscription?.addon_bundles || [];

  useEffect(() => {
    api.get('/billing/subscription').then(r => setSubscription(r.data)).catch(() => {});
    api.get('/organizations/addon-bundles').then(r => setAddonBundles(r.data.bundles || [])).catch(() => {});
  }, []);

  const handleAutoRenewToggle = async (val) => {
    setAutoRenewSaving(true);
    try {
      await api.patch('/billing/subscription', { auto_renew: val });
      setSubscription(s => ({ ...s, auto_renew: val }));
      toast.success(`Auto-renew ${val ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update'); }
    setAutoRenewSaving(false);
  };

  const handleCheckoutSuccess = () => {
    toast.success('Subscription activated!');
    api.get('/billing/subscription').then(r => setSubscription(r.data)).catch(() => {});
    setTimeout(() => navigate('/'), 2000);
  };

  const planSelected = PLANS.find(p => p.id === checkoutPlan);

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: C.bg, minHeight: '100vh' }}>
      {/* Subtle wheat texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(https://images.pexels.com/photos/8603000/pexels-photo-8603000.jpeg)`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          opacity: 0.04,
          zIndex: 0,
        }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">

        {/* Top nav */}
        <div className="flex items-center justify-between mb-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-70"
            style={{ color: C.text2 }}
          >
            <ArrowLeft size={16} /> Back to Dashboard
          </button>
          {currentPlan !== 'trial' && (
            <span
              className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
              style={{ backgroundColor: C.bg2, color: C.text }}
            >
              Current: {currentPlan}
            </span>
          )}
        </div>

        {/* Page header */}
        <div className="text-center mb-12 space-y-3">
          <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: C.accent }}>
            Pricing & Plans
          </p>
          <h1
            className="text-4xl sm:text-5xl font-black leading-tight"
            style={{ color: C.text, fontFamily: 'Outfit, Manrope, sans-serif' }}
          >
            Grow with the right plan
          </h1>
          <p className="text-base max-w-xl mx-auto" style={{ color: C.text2 }}>
            All plans include full offline POS, inventory, and customer management.
            Upgrade when you're ready.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <span
            className="text-sm font-semibold cursor-pointer select-none"
            style={{ color: !annual ? C.text : C.text2 }}
            onClick={() => setAnnual(false)}
          >
            Monthly
          </span>
          <button
            onClick={() => setAnnual(a => !a)}
            className="relative w-14 h-7 rounded-full transition-colors duration-300"
            style={{ backgroundColor: annual ? C.success : C.border }}
            data-testid="billing-toggle"
            aria-label="Toggle annual billing"
          >
            <div
              className="absolute top-[3px] w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-300"
              style={{ left: annual ? 'calc(100% - 23px)' : '3px' }}
            />
          </button>
          <span className="flex items-center gap-2 cursor-pointer select-none" onClick={() => setAnnual(true)}>
            <span className="text-sm font-semibold" style={{ color: annual ? C.text : C.text2 }}>Annual</span>
            <span
              className="text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide"
              style={{ backgroundColor: annual ? C.gold : C.bg2, color: annual ? '#2D3728' : C.text2 }}
            >
              2 months free
            </span>
          </span>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {PLANS.map((plan, idx) => {
            const php = annual ? plan.annualPhp : plan.monthlyPhp;
            const phpPerMonth = annual ? Math.round(plan.annualPhp / 12) : plan.monthlyPhp;
            const isCurrentPlan = currentPlan === plan.id
              || (currentPlan === 'basic' && plan.id === 'starter')
              || (currentPlan === 'standard' && plan.id === 'business')
              || (currentPlan === 'pro' && plan.id === 'professional');
            const PlanIcon = plan.icon;

            return (
              <div
                key={plan.id}
                className="rounded-3xl p-6 flex flex-col gap-4 relative overflow-hidden transition-all duration-200"
                style={{
                  backgroundColor: plan.dark ? C.dark : 'white',
                  border: `2px solid ${plan.highlight ? C.gold : plan.dark ? 'rgba(248,246,243,0.15)' : C.border}`,
                  boxShadow: plan.highlight ? `0 20px 60px rgba(0,0,0,0.2)` : '0 2px 12px rgba(0,0,0,0.06)',
                  transform: plan.highlight ? 'translateY(-6px)' : undefined,
                }}
                data-testid={`plan-card-${plan.id}`}
              >
                {/* Terracotta texture overlay for Professional */}
                {plan.dark && (
                  <div
                    className="absolute inset-0 pointer-events-none rounded-3xl"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                      backgroundSize: '140px 140px', opacity: 0.07,
                    }}
                  />
                )}

                {plan.highlight && (
                  <div
                    className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                    style={{ backgroundColor: C.gold, color: C.dark }}
                  >
                    Most Popular
                  </div>
                )}

                {isCurrentPlan && (
                  <div
                    className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[9px] font-black uppercase"
                    style={{ backgroundColor: plan.dark ? 'rgba(248,246,243,0.2)' : C.bg2, color: plan.dark ? 'white' : C.text }}
                  >
                    Current
                  </div>
                )}

                {/* Icon + name */}
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: plan.dark ? 'rgba(248,246,243,0.15)' : C.bg2 }}
                  >
                    <PlanIcon size={18} style={{ color: plan.dark ? C.gold : plan.color }} />
                  </div>
                  <div>
                    <p
                      className="font-black text-base leading-tight"
                      style={{ color: plan.dark ? 'white' : C.text }}
                    >
                      {plan.name}
                    </p>
                    <p className="text-[11px]" style={{ color: plan.dark ? 'rgba(248,246,243,0.6)' : C.text2 }}>
                      {plan.tagline}
                    </p>
                  </div>
                </div>

                {/* Price */}
                <div>
                  {annual ? (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span
                          className="text-3xl font-black"
                          style={{ color: plan.dark ? 'white' : C.text, fontVariantNumeric: 'tabular-nums' }}
                        >
                          ₱{phpPerMonth.toLocaleString()}
                        </span>
                        <span className="text-xs" style={{ color: plan.dark ? 'rgba(248,246,243,0.5)' : C.text2 }}>/mo</span>
                      </div>
                      <p className="text-[11px] mt-0.5" style={{ color: plan.dark ? C.gold : C.accent }}>
                        ₱{php.toLocaleString()} billed annually
                      </p>
                    </>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span
                        className="text-3xl font-black"
                        style={{ color: plan.dark ? 'white' : C.text, fontVariantNumeric: 'tabular-nums' }}
                      >
                        ₱{php.toLocaleString()}
                      </span>
                      <span className="text-xs" style={{ color: plan.dark ? 'rgba(248,246,243,0.5)' : C.text2 }}>/mo</span>
                    </div>
                  )}
                </div>

                {/* Limits */}
                <div
                  className="text-[11px] flex flex-col gap-1 pt-3"
                  style={{ borderTop: `1px solid ${plan.dark ? 'rgba(248,246,243,0.1)' : C.border}` }}
                >
                  {[
                    { label: 'Branches', val: plan.branches },
                    { label: 'Users', val: plan.users },
                    { label: 'Products', val: plan.products },
                  ].map(({ label, val }) => (
                    <div key={label} className="flex justify-between">
                      <span style={{ color: plan.dark ? 'rgba(248,246,243,0.5)' : C.text2 }}>{label}</span>
                      <span className="font-bold" style={{ color: plan.dark ? 'white' : C.text }}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <button
                  className="mt-auto w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-150 active:scale-95"
                  style={{
                    backgroundColor: isCurrentPlan
                      ? (plan.dark ? 'rgba(248,246,243,0.1)' : C.bg2)
                      : plan.dark ? C.gold : C.accent,
                    color: isCurrentPlan
                      ? (plan.dark ? 'rgba(248,246,243,0.4)' : C.text2)
                      : plan.dark ? C.dark : 'white',
                    cursor: isCurrentPlan ? 'not-allowed' : 'pointer',
                  }}
                  onClick={() => !isCurrentPlan && setCheckoutPlan(plan.id)}
                  disabled={isCurrentPlan}
                  data-testid={`plan-checkout-btn-${plan.id}`}
                >
                  {isCurrentPlan ? 'Current Plan' : 'Get Started →'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Per-branch add-on calculator */}
        <div
          className="rounded-2xl p-6 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ backgroundColor: C.bg2, border: `1px solid ${C.border}` }}
        >
          <div>
            <p className="font-bold text-sm" style={{ color: C.text }}>Per-Branch Add-on</p>
            <p className="text-xs mt-0.5" style={{ color: C.text2 }}>
              Professional +₱2,000/branch · Enterprise +₱3,000/branch · Billed same as base plan
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium" style={{ color: C.text2 }}>Extra branches</span>
            <div
              className="flex items-center rounded-xl overflow-hidden border"
              style={{ borderColor: C.border, backgroundColor: 'white' }}
            >
              <button
                className="px-4 py-2 text-sm font-bold hover:opacity-70 transition-opacity"
                style={{ color: C.text }}
                onClick={() => setExtraBranches(b => Math.max(0, b - 1))}
                data-testid="extra-branches-decrement"
              >
                −
              </button>
              <span
                className="px-4 py-2 font-black text-base min-w-[3rem] text-center"
                style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}
                data-testid="extra-branches-count"
              >
                {extraBranches}
              </span>
              <button
                className="px-4 py-2 text-sm font-bold hover:opacity-70 transition-opacity"
                style={{ color: C.text }}
                onClick={() => setExtraBranches(b => b + 1)}
                data-testid="extra-branches-increment"
              >
                +
              </button>
            </div>
            {extraBranches > 0 && (
              <span className="text-xs font-bold" style={{ color: C.accent }}>
                +₱{(extraBranches * 2000).toLocaleString()}/mo (Pro)
              </span>
            )}
          </div>
        </div>

        {/* Power-ups: Add-on bundles */}
        {addonBundles.length > 0 && (
          <div className="mb-8 space-y-3">
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: C.accent }}>Power-ups</p>
              <h3 className="text-xl font-black mt-1" style={{ color: C.text }}>Add features without upgrading</h3>
              <p className="text-sm mt-1" style={{ color: C.text2 }}>Standalone bundles that work on any plan. Pay only for what you need.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {addonBundles.map(bundle => {
                const isActive = activeAddonBundles.includes(bundle.id);
                const monthlyPrice = bundle.addon_price_php || 0;
                const annualPrice = Math.round(monthlyPrice * 10);
                const displayPrice = annual ? Math.round(annualPrice / 12) : monthlyPrice;
                return (
                  <div
                    key={bundle.id}
                    className="rounded-2xl p-5 flex flex-col gap-3 relative"
                    style={{
                      backgroundColor: 'white',
                      border: `2px solid ${isActive ? C.success : C.border}`,
                      boxShadow: isActive ? `0 4px 20px rgba(71,114,66,0.15)` : '0 2px 8px rgba(0,0,0,0.04)',
                    }}
                    data-testid={`addon-bundle-${bundle.id}`}
                  >
                    {isActive && (
                      <span className="absolute top-3 right-3 text-[9px] font-black px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: C.success + '20', color: C.success }}>ACTIVE</span>
                    )}
                    <div>
                      <p className="font-black text-sm" style={{ color: C.text }}>{bundle.label}</p>
                      <p className="text-xs mt-0.5" style={{ color: C.text2 }}>{bundle.description}</p>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-black" style={{ color: C.text, fontVariantNumeric: 'tabular-nums' }}>₱{displayPrice.toLocaleString()}</span>
                      <span className="text-xs" style={{ color: C.text2 }}>/mo</span>
                      {annual && <span className="text-[10px] font-bold ml-1" style={{ color: C.accent }}>₱{annualPrice.toLocaleString()} billed annually</span>}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {bundle.features.slice(0, 4).map(f => (
                        <span key={f} className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: C.bg2, color: C.text2 }}>{f}</span>
                      ))}
                      {bundle.features.length > 4 && <span className="text-[10px]" style={{ color: C.text2 }}>+{bundle.features.length - 4} more</span>}
                    </div>
                    {bundle.requires_plan && (
                      <p className="text-[10px]" style={{ color: C.text2 }}>Requires {bundle.requires_plan} plan as base</p>
                    )}
                    <button
                      className="mt-auto w-full py-2 rounded-xl text-xs font-bold transition-all active:scale-95"
                      style={{
                        backgroundColor: isActive ? C.bg2 : C.accent,
                        color: isActive ? C.text2 : 'white',
                        cursor: isActive ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => !isActive && setCheckoutAddon(bundle)}
                      disabled={isActive}
                      data-testid={`addon-checkout-btn-${bundle.id}`}
                    >
                      {isActive ? 'Already Active' : 'Add to my plan →'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Feature comparison table */}
        <div
          className="rounded-2xl overflow-hidden mb-8"
          style={{ border: `1px solid ${C.border}` }}
          data-testid="feature-comparison-table"
        >
          {/* Table header */}
          <div className="grid" style={{ gridTemplateColumns: '1fr repeat(4, 100px)', backgroundColor: 'white' }}>
            <div className="p-4 font-bold text-sm" style={{ color: C.text }}>Features</div>
            {PLANS.map((plan, idx) => (
              <div
                key={plan.id}
                className="p-4 text-center"
                style={{ backgroundColor: plan.dark ? C.dark : idx % 2 === 0 ? C.bg : 'white' }}
              >
                <p className="text-xs font-black uppercase tracking-wide" style={{ color: plan.dark ? C.gold : C.text }}>
                  {plan.name}
                </p>
              </div>
            ))}
          </div>

          {/* Feature groups */}
          {FEATURE_GROUPS.slice(0, tableExpanded ? undefined : 3).map((group) => (
            <div key={group.category}>
              {/* Category header */}
              <div
                className="grid items-center"
                style={{ gridTemplateColumns: '1fr repeat(4, 100px)', backgroundColor: C.bg2, borderTop: `1px solid ${C.border}` }}
              >
                <div className="px-4 py-2.5 flex items-center gap-2">
                  <group.icon size={13} style={{ color: C.accent }} />
                  <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: C.text }}>
                    {group.category}
                  </span>
                </div>
                {PLANS.map((plan, idx) => (
                  <div
                    key={plan.id}
                    style={{ backgroundColor: plan.dark ? 'rgba(45,55,40,0.95)' : idx % 2 === 0 ? C.bg2 : C.bg2 }}
                    className="py-2.5"
                  />
                ))}
              </div>

              {/* Feature rows */}
              {group.features.map((feat, fIdx) => (
                <div
                  key={feat.label}
                  className="grid items-center"
                  style={{
                    gridTemplateColumns: '1fr repeat(4, 100px)',
                    borderTop: `1px solid ${C.border}`,
                    backgroundColor: fIdx % 2 === 0 ? 'white' : C.bg,
                  }}
                >
                  <div className="px-4 py-2.5 text-[12px]" style={{ color: C.text2 }}>{feat.label}</div>
                  {feat.plans.map((val, pIdx) => (
                    <div
                      key={pIdx}
                      className="py-2.5"
                      style={{
                        backgroundColor: PLANS[pIdx].dark
                          ? (fIdx % 2 === 0 ? C.dark : 'rgba(45,55,40,0.92)')
                          : 'transparent',
                      }}
                    >
                      <FeatureCell val={val} planIdx={pIdx} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}

          {/* Expand/collapse */}
          <button
            className="w-full py-3 flex items-center justify-center gap-2 text-sm font-semibold transition-colors hover:opacity-70"
            style={{ color: C.text2, backgroundColor: C.bg2, borderTop: `1px solid ${C.border}` }}
            onClick={() => setTableExpanded(e => !e)}
            data-testid="table-expand-btn"
          >
            {tableExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {tableExpanded ? 'Show less' : `Show all ${FEATURE_GROUPS.reduce((a, g) => a + g.features.length, 0)} features`}
          </button>
        </div>

        {/* Billing section (if subscription exists) */}
        {subscription && subscription.subscription_status !== 'trial' && (
          <div
            className="rounded-2xl p-6 mb-8 space-y-4"
            style={{ backgroundColor: 'white', border: `1px solid ${C.border}` }}
            data-testid="billing-section"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-black text-base" style={{ color: C.text }}>Current Billing</p>
                <p className="text-xs mt-0.5" style={{ color: C.text2 }}>
                  Plan: <strong>{(() => {
                    const pid = subscription.billing_plan_id || '';
                    const nameMap = { starter_monthly: 'Starter', starter_annual: 'Starter (Annual)', business_monthly: 'Business', business_annual: 'Business (Annual)', professional_monthly: 'Professional', professional_annual: 'Professional (Annual)', enterprise_monthly: 'Enterprise', enterprise_annual: 'Enterprise (Annual)' };
                    const planMap = { basic: 'Starter', starter: 'Starter', standard: 'Business', business: 'Business', pro: 'Professional', professional: 'Professional', enterprise: 'Enterprise' };
                    return nameMap[pid] || planMap[subscription.plan] || subscription.plan;
                  })()}</strong>
                  {subscription.subscription_valid_until && (
                    <> · Renews {formatDate(subscription.subscription_valid_until)}</>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs" style={{ color: C.text2 }}>Auto-renew</span>
                <button
                  onClick={() => !autoRenewSaving && handleAutoRenewToggle(!subscription.auto_renew)}
                  className="relative w-11 h-6 rounded-full transition-colors"
                  style={{ backgroundColor: subscription.auto_renew ? C.success : C.border }}
                  data-testid="auto-renew-toggle"
                  disabled={autoRenewSaving}
                >
                  <div
                    className="absolute top-[2px] w-5 h-5 bg-white rounded-full shadow transition-transform"
                    style={{ left: subscription.auto_renew ? 'calc(100% - 22px)' : '2px' }}
                  />
                </button>
              </div>
            </div>

            {subscription.auto_renew === false && (
              <div
                className="text-xs px-4 py-2 rounded-xl"
                style={{ backgroundColor: '#fef3c7', color: '#92400e' }}
              >
                Auto-renew is off. Your subscription will not renew automatically. You can re-enable it anytime.
              </div>
            )}
          </div>
        )}

        {/* Grace period / trial warning */}
        {(currentPlan === 'grace_period' || currentPlan === 'soft_locked' || currentPlan === 'trial') && (
          <div
            className="rounded-2xl p-5 mb-8 flex items-start gap-4"
            style={{
              backgroundColor: currentPlan === 'soft_locked' ? '#fef2f2' : '#fef9c3',
              border: `1px solid ${currentPlan === 'soft_locked' ? '#fecaca' : '#fde68a'}`,
            }}
            data-testid="plan-warning-banner"
          >
            <div>
              <p className="font-bold text-sm" style={{ color: currentPlan === 'soft_locked' ? '#991b1b' : '#92400e' }}>
                {currentPlan === 'soft_locked'
                  ? 'Account locked — subscription required to continue transacting'
                  : currentPlan === 'grace_period'
                  ? '7-day grace period — subscription overdue'
                  : `Trial active — ${user?.subscription?.trial_days_left || ''} days remaining`}
              </p>
              <p className="text-xs mt-1" style={{ color: currentPlan === 'soft_locked' ? '#b91c1c' : '#b45309' }}>
                {currentPlan === 'soft_locked'
                  ? 'You can still view all your data. Subscribe to a plan above to resume operations.'
                  : 'Select a plan above to keep all features after your period ends.'}
              </p>
            </div>
          </div>
        )}

        {/* FAQ / trust footer */}
        <div
          className="rounded-2xl p-6 text-center space-y-2"
          style={{ backgroundColor: C.bg2 }}
        >
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.text2 }}>Payment</p>
          <p className="text-sm" style={{ color: C.text }}>
            Pay via QR Ph — scan with GCash, Maya, BDO, BPI, UnionBank and any QR-enabled banking app.
          </p>
          <p className="text-xs" style={{ color: C.text2 }}>
            Subscription activates instantly after QR scan. 7-day grace period included.
          </p>
        </div>
      </div>

      {/* QR Checkout Dialog — Plan */}
      {checkoutPlan && planSelected && (
        <QRCheckoutDialog
          plan={planSelected}
          annual={annual}
          extraBranches={planSelected.extraBranchAvailable ? extraBranches : 0}
          onClose={() => setCheckoutPlan(null)}
          onSuccess={handleCheckoutSuccess}
        />
      )}

      {/* QR Checkout Dialog — Add-on Bundle */}
      {checkoutAddon && (
        <AddonCheckoutDialog
          bundle={checkoutAddon}
          annual={annual}
          onClose={() => setCheckoutAddon(null)}
          onSuccess={() => { toast.success(`${checkoutAddon.label} bundle activated!`); setCheckoutAddon(null); }}
        />
      )}
    </div>
  );
}
